import { t as tr } from '../i18n.js'
import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useParams } from 'react-router'
import { api, ApiError, type ApprovalPeriod, type ApprovalWarnings, type ProjectDetail } from '../api.js'
import { fmtMinutes } from '../time.js'
import { fmt } from './Account.js'
import { ProjectTabs } from './ProjectTabs.js'
import { atLeast, useAuth } from '../auth.js'
import { StateChip } from './Timesheets.js'

/** Project administrators: review member weeks, approve/reject, force-approve with guardrails, reopen. */
export function ProjectApprovalsPage() {
  const { projectId = '' } = useParams()
  const { user } = useAuth()
  const qc = useQueryClient()
  const detail = useQuery({ queryKey: ['project', projectId], queryFn: () => api<ProjectDetail>(`/projects/${projectId}`), retry: false })
  const [state, setState] = useState('')
  const [weeks, setWeeks] = useState(8)
  const q = useQuery({ queryKey: ['approvals', projectId, state, weeks], queryFn: () => api<{ periods: ApprovalPeriod[] }>(`/projects/${projectId}/approvals?weeksBack=${weeks}${state ? `&state=${state}` : ''}`), retry: false })
  const [err, setErr] = useState<string | null>(null)
  const [pending, setPending] = useState<{ p: ApprovalPeriod; w: ApprovalWarnings } | null>(null)
  const invalidate = () => qc.invalidateQueries({ queryKey: ['approvals'] })
  const decide = useMutation({
    mutationFn: (b: Record<string, unknown>) => api<{ period: ApprovalPeriod; warnings: ApprovalWarnings | null }>(`/projects/${projectId}/approvals/decide`, { method: 'POST', json: b }),
    onSuccess: () => { setPending(null); void invalidate() },
    onError: (e) => setErr(e instanceof ApiError ? e.message : 'Request failed'),
  })
  const reopen = useMutation({ mutationFn: (b: Record<string, unknown>) => api(`/projects/${projectId}/approvals/reopen`, { method: 'POST', json: b }), onSuccess: invalidate, onError: (e) => setErr(e instanceof ApiError ? e.message : 'Request failed') })

  const forceApprove = async (p: ApprovalPeriod) => {
    setErr(null)
    const w = await api<ApprovalWarnings>(`/projects/${projectId}/approvals/preview?userId=${p.userId}&weekStart=${p.weekStart}`)
    if (w.empty || w.underCapacity) setPending({ p, w })
    else decide.mutate({ userId: p.userId, weekStart: p.weekStart, decision: 'approve', force: true })
  }

  if (detail.isError || q.isError) return <p className="bad">{tr("Not available — approvals are for project administrators.")} <Link to={`/projects/${projectId}`}>{tr("Back")}</Link></p>
  if (!detail.data) return <p className="muted">{tr("Loading…")}</p>
  const p = detail.data.project
  const rows = q.data?.periods ?? []
  return (
    <div className="boardpage">
      <p className="crumbs"><Link to="/projects">{tr("Projects")}</Link> / {p.code}</p>
      <ProjectTabs projectId={p.id} active="approvals" canApprove canSeeRates={atLeast(user, 'manager')} />
      <div className="row toolbar">
        <select value={state} onChange={(e) => setState(e.target.value)}><option value="">{tr("All states")}</option><option value="submitted">{tr("Awaiting approval")}</option><option value="open">{tr("Not submitted")}</option><option value="rejected">{tr("Sent back")}</option><option value="approved">{tr("Approved")}</option></select>
        <label className="inline small">{tr("Last")} <select value={weeks} onChange={(e) => setWeeks(Number(e.target.value))}><option value={4}>4</option><option value={8}>8</option><option value={13}>13</option><option value={26}>26</option></select> weeks</label>
        <span className="muted small">{tr("Weeks run Monday→Monday in")} <strong>{p.timezoneOfTruth}</strong>. Approving locks the week; late work then arrives as corrections.</span>
      </div>
      {err && <p className="bad small">{err}</p>}
      {pending && (
        <section className="card stack notice bad-border">
          <p><strong>{tr("Approve without submission?")}</strong> {pending.p.userName}, week of {pending.p.weekStart}: {pending.w.empty ? 'no time logged' : `${fmtMinutes(pending.w.minutes)} logged against ${pending.w.capacityHours}h capacity`}. They will be notified and the week will lock.</p>
          <div className="row">
            <button className="button primary" onClick={() => decide.mutate({ userId: pending.p.userId, weekStart: pending.p.weekStart, decision: 'approve', force: true, acknowledgeWarnings: true })}>Approve anyway</button>
            <button className="button" onClick={() => setPending(null)}>Cancel</button>
          </div>
        </section>
      )}
      <section className="card">
        {rows.length === 0 && <p className="muted small">{tr("Nothing here.")}</p>}
        {rows.length > 0 && (
          <table className="table">
            <thead><tr><th>{tr("Week of")}</th><th>{tr("Person")}</th><th className="num">{tr("Hours")}</th><th className="num">{tr("Capacity")}</th><th>{tr("State")}</th><th>{tr("Notes")}</th><th></th></tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={`${r.userId}-${r.weekStart}`}>
                  <td className="mono">{r.weekStart}{r.isCurrentWeek && <span className="tag">{tr("in progress")}</span>}</td>
                  <td>{r.userName}</td>
                  <td className={`num mono ${r.minutes === 0 ? 'muted' : ''}`}>{fmtMinutes(r.minutes)} <span className="muted small">({r.entryCount})</span></td>
                  <td className="num mono muted">{r.capacityHours}h</td>
                  <td><StateChip state={r.state} forced={r.forceApproved} /></td>
                  <td className="small muted">
                    {r.submitNote && <>note: {r.submitNote} </>}
                    {r.state === 'approved' && <>by {r.decidedByName} {fmt(r.decidedAt)}</>}
                    {r.state === 'rejected' && <span className="bad">{r.decisionNote}</span>}
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {r.state === 'submitted' && <>
                      <button className="button primary" data-tip={tr("Approve — locks the week; any later work must be a correction entry")} onClick={() => { setErr(null); decide.mutate({ userId: r.userId, weekStart: r.weekStart, decision: 'approve' }) }}>Approve</button>{' '}
                      <button className="button" data-tip={tr("Return the week with a note; the person edits and resubmits")} onClick={() => { const note = prompt('What should they fix?'); if (note) { setErr(null); decide.mutate({ userId: r.userId, weekStart: r.weekStart, decision: 'reject', note }) } }}>Send back</button>
                    </>}
                    {(r.state === 'open' || r.state === 'rejected') && !r.isCurrentWeek && <button className="link" onClick={() => void forceApprove(r)} data-tip={tr("Approve although it was not submitted — you'll be warned if the week is empty or thin, and the person is notified")}>Approve unsubmitted…</button>}
                    {r.state === 'approved' && <button className="link danger" data-tip={tr("Unlock an approved week for edits; requires a reason, recorded in the audit log, and notifies the person")} onClick={() => { const note = prompt('Why reopen this approved week? (recorded in the audit log)'); if (note && note.trim().length >= 3) reopen.mutate({ userId: r.userId, weekStart: r.weekStart, note }) }}>Reopen…</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  )
}
